import Breadcrumb from "component/common/breadcrumb";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import strings from "translate/language";
import { bookingStatus } from "utils/constants";
import { handleDetailResponse, seo } from "utils/utils";
import { getBookingDetails } from "../../reduxStore/action/bookingActions";
import bookingService from "../../reduxStore/service/bookingService";
import { toast } from "react-toastify";
import "./BookingListing.css";

const getBookingStatusBadgeClass = (status) => {
  const value = Number(status);
  if (value === 1) return "booking-status-badge--ready";
  if (value === 2) return "booking-status-badge--booking";
  if (value === 3) return "booking-status-badge--complete";
  if (value === 4) return "booking-status-badge--cancelled";
  return "booking-status-badge--default";
};

const BookingStatusIcon = ({ tone = "success" }) => (
  <span className="booking-status-badge__icon" aria-hidden="true">
    <i
      className={
        tone === "failure" ? "fa fa-exclamation-circle" : "fa fa-check-circle"
      }
    />
  </span>
);

const getBookingStatusIconTone = (status) =>
  Number(status) === 4 ? "failure" : "success";

const displayValue = (value) => {
  if (value == null || value === "" || value === "NA") {
    return strings.notavailable;
  }
  return value;
};

const isEmpty = (value) =>
  value == null || value === "" || value === "NA";

const ViewBooking = () => {
  const dispatch = useDispatch();
  const [bookingDetails, setBookingDetails] = useState({});
  const [syncing, setSyncing] = useState(false);
  const params = useParams();
  const { booking_id = "" } = params || {};

  const BookingDetailSelector = useSelector((state) => state.booking);

  useEffect(() => {
    dispatch(getBookingDetails({ booking_id }));
  }, [booking_id, dispatch]);

  useEffect(() => {
    if (!BookingDetailSelector?.detail || !BookingDetailSelector.response) {
      return;
    }
    const { response } = BookingDetailSelector;
    if (response?.status === 200) {
      setBookingDetails(handleDetailResponse(response?.data));
    } else {
      setBookingDetails({});
    }
  }, [BookingDetailSelector]);

  useEffect(() => {
    seo({
      title: "Booking Details",
      metaDescription: "Booking details",
      canonicalLink: window.location.href,
    });
  }, []);

  const needsJwSync =
    bookingDetails?._id &&
    Number(bookingDetails?.booking_status) !== 4 &&
    (!bookingDetails?.booking_jw_no ||
      String(bookingDetails.booking_jw_no).trim() === "");

  const handleSyncToJw = async () => {
    if (!bookingDetails?._id || syncing) return;
    setSyncing(true);
    try {
      const res = await bookingService.syncBookingToJW({
        booking_id: bookingDetails._id,
      });
      if (res?.data?.status === 200) {
        toast.success(res?.data?.message || strings.jwSyncSuccess);
        dispatch(getBookingDetails({ booking_id }));
      } else {
        toast.error(
          res?.data?.message || "JW sync failed. Please try again.",
        );
      }
    } catch (e) {
      toast.error(e?.message || "JW sync failed. Please try again.");
    } finally {
      setSyncing(false);
    }
  };

  const isJwSynced =
    bookingDetails?.booking_jw_no &&
    String(bookingDetails.booking_jw_no).trim() !== "";

  const statusLabel =
    bookingStatus[bookingDetails?.booking_status] || strings.notavailable;

  const propertyTitle =
    bookingDetails.properties?.property_title || strings.viewBooking;

  const budgetValue = bookingDetails?.budget_amount
    ? bookingDetails.budget_amount
    : bookingDetails?.min_budget && bookingDetails?.max_budget
      ? `${bookingDetails.min_budget}${bookingDetails.min_budget_type} - ${bookingDetails.max_budget}${bookingDetails.max_budget_type}`
      : "";

  const configQty =
    bookingDetails?.configuration && bookingDetails?.quantity
      ? `${bookingDetails.configuration} / ${bookingDetails.quantity}`
      : bookingDetails?.configuration || "";

  const jwDisplay =
    Number(bookingDetails?.booking_status) === 4
      ? "-"
      : isJwSynced
        ? String(bookingDetails.booking_jw_no)
        : strings.jwSyncPendingShort;

  const detailRows = [
    { label: strings.bookingBy, value: bookingDetails?.booking_by_name },
    { label: strings.configuration, value: bookingDetails.configuration },
    { label: strings.Area, value: bookingDetails.area },
    { label: strings.confiQty, value: configQty },
    { label: strings.budget, value: budgetValue },
    { label: strings.PaymentType, value: bookingDetails?.payment_type },
    {
      label: strings.chequeNumber,
      value: bookingDetails?.tranjection_upi_cheque_number,
    },
    { label: strings.bookingNumber, value: bookingDetails?.booking_no },
  ];

  return (
    <div className="page-body justo-booking-page">
      <Breadcrumb
        title={strings.booking}
        url={"/booking_list"}
        parentListing={strings.bookingListing}
        titleactive={strings.viewBooking}
        isBackBtn={true}
      />

      <div className="container-fluid justo-booking">
        <div className="justo-booking__card justo-booking-detail">
          <div className="justo-booking-detail__hero">
            <div className="justo-booking-detail__hero-main">
              <p className="justo-booking-detail__eyebrow">
                {strings.viewBooking}
              </p>
              <h1 className="justo-booking-detail__property">{propertyTitle}</h1>
              <p className="justo-booking-detail__meta">
                {bookingDetails?.booking_no ? (
                  <span>
                    {strings.bookingNumber}:{" "}
                    <strong>{bookingDetails.booking_no}</strong>
                  </span>
                ) : null}
                {bookingDetails?.booking_by_name ? (
                  <span>
                    {strings.bookingBy}:{" "}
                    <strong>{bookingDetails.booking_by_name}</strong>
                  </span>
                ) : null}
              </p>
            </div>

            <div className="justo-booking-detail__hero-actions">
              {bookingDetails?.booking_status != null &&
              bookingDetails?.booking_status !== "" ? (
                <span
                  className={`booking-status-badge ${getBookingStatusBadgeClass(
                    bookingDetails.booking_status,
                  )}`}
                  title={statusLabel}
                >
                  <BookingStatusIcon
                    tone={getBookingStatusIconTone(
                      bookingDetails.booking_status,
                    )}
                  />
                  <span className="booking-status-badge__label">
                    {statusLabel}
                  </span>
                </span>
              ) : null}

              {Number(bookingDetails?.booking_status) === 4 ? null : isJwSynced ? (
                <div className="justo-booking-detail__jw-status">
                  <span
                    className="booking-status-badge booking-status-badge--synced"
                    title={`${strings.jwSynced}: ${bookingDetails.booking_jw_no}`}
                  >
                    <BookingStatusIcon tone="success" />
                    <span className="booking-status-badge__label">
                      {strings.jwSyncedShort}
                    </span>
                  </span>
                  <span className="booking-listing__jw-no">
                    {String(bookingDetails.booking_jw_no)}
                  </span>
                </div>
              ) : bookingDetails?._id ? (
                <div className="justo-booking-detail__jw-status">
                  <span
                    className="booking-status-badge booking-status-badge--pending"
                    title={strings.jwSyncPending}
                  >
                    <BookingStatusIcon tone="failure" />
                    <span className="booking-status-badge__label">
                      {strings.jwSyncPendingShort}
                    </span>
                  </span>
                </div>
              ) : null}

              {needsJwSync ? (
                <button
                  type="button"
                  className="justo-booking__btn justo-booking__btn--solid justo-booking__btn--sm"
                  disabled={syncing}
                  onClick={handleSyncToJw}
                >
                  {syncing ? "Syncing..." : strings.syncToJw}
                </button>
              ) : null}
            </div>
          </div>

          <div className="justo-booking-detail__stats">
            <div className="justo-booking-detail__stat">
              <span className="justo-booking-detail__stat-label">
                {strings.visitScore}
              </span>
              <span className="justo-booking-detail__stat-value">
                {bookingDetails?.leads?.lead_score ?? 0}
              </span>
            </div>
            <div className="justo-booking-detail__stat">
              <span className="justo-booking-detail__stat-label">
                {strings.bookingAmount}
              </span>
              <span
                className={`justo-booking-detail__stat-value${
                  isEmpty(bookingDetails?.booking_amount) ? " is-muted" : ""
                }`}
              >
                {displayValue(bookingDetails?.booking_amount)}
              </span>
            </div>
            <div className="justo-booking-detail__stat">
              <span className="justo-booking-detail__stat-label">
                {strings.configuration}
              </span>
              <span
                className={`justo-booking-detail__stat-value${
                  isEmpty(bookingDetails?.configuration) ? " is-muted" : ""
                }`}
              >
                {displayValue(bookingDetails?.configuration)}
              </span>
            </div>
            <div className="justo-booking-detail__stat">
              <span className="justo-booking-detail__stat-label">
                {strings.Area}
              </span>
              <span
                className={`justo-booking-detail__stat-value${
                  isEmpty(bookingDetails?.area) ? " is-muted" : ""
                }`}
              >
                {displayValue(bookingDetails?.area)}
              </span>
            </div>
          </div>

          <div className="justo-booking-detail__panel">
            <div className="justo-booking-detail__panel-head">
              <h2 className="justo-booking-detail__panel-title">
                {strings.bookingDetailOverview}
              </h2>
              <p className="justo-booking-detail__panel-copy">
                {strings.bookingDetailsSubtitle}
              </p>
            </div>
            <dl className="justo-booking-detail__list">
              {detailRows.map((row) => {
                const empty = isEmpty(row.value);
                return (
                  <div className="justo-booking-detail__row" key={row.label}>
                    <dt>{row.label}</dt>
                    <dd className={empty ? "is-muted" : undefined}>
                      {displayValue(row.value)}
                    </dd>
                  </div>
                );
              })}
              <div className="justo-booking-detail__row">
                <dt>{strings.jwBookingNo}</dt>
                <dd>
                  {Number(bookingDetails?.booking_status) === 4 ? (
                    <span className="is-muted">-</span>
                  ) : isJwSynced ? (
                    <div className="justo-booking-detail__jw-status justo-booking-detail__jw-status--inline">
                      <span
                        className="booking-status-badge booking-status-badge--synced"
                        title={strings.jwSynced}
                      >
                        <BookingStatusIcon tone="success" />
                        <span className="booking-status-badge__label">
                          {strings.jwSyncedShort}
                        </span>
                      </span>
                      <span className="booking-listing__jw-no">
                        {String(bookingDetails.booking_jw_no)}
                      </span>
                    </div>
                  ) : (
                    <span
                      className="booking-status-badge booking-status-badge--pending"
                      title={strings.jwSyncPending}
                    >
                      <BookingStatusIcon tone="failure" />
                      <span className="booking-status-badge__label">
                        {strings.jwSyncPendingShort}
                      </span>
                    </span>
                  )}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewBooking;
